import { useState } from 'react';
import { Phone } from 'lucide-react';
import { cn } from '@/lib/utils';
import { CallContactDialog } from './CallContactDialog';
import { EmailContactDialog } from './EmailContactDialog';

interface Props {
  phone?: string | null;
  agentName?: string | null;
  agencyName?: string | null;
  agentLogo?: string | null;
  referenceId?: string | null;
  propertyId?: string;
  projectId?: string;
  listingType?: 'property' | 'project' | 'plot' | 'commercial';
  size?: 'sm' | 'md';
  className?: string;
}

/**
 * Three-button compact contact action row used on condensed listing cards:
 * Message (outline primary) · WhatsApp (brand green) · Call (solid primary)
 */
export function CompactContactActions({
  phone,
  agentName,
  agencyName,
  agentLogo,
  referenceId,
  propertyId,
  projectId,
  listingType = 'property',
  size = 'sm',
  className,
}: Props) {
  const [callOpen, setCallOpen] = useState(false);
  const [msgOpen, setMsgOpen] = useState(false);
  const wa = phone?.replace(/[^\d]/g, '');
  const h = size === 'md' ? 'h-9' : 'h-8';
  const icon = size === 'md' ? 'h-4 w-4' : 'h-4 w-4';

  const stop = (e: React.MouseEvent) => { e.preventDefault(); e.stopPropagation(); };

  return (
    <>
      <div className={cn('flex items-center gap-2 w-full', className)} onClick={stop}>
        <button
          type="button"
          onClick={(e) => { stop(e); setMsgOpen(true); }}
          className={cn('inline-flex items-center justify-center rounded-md border border-primary/40 bg-background text-primary hover:bg-primary/5 transition-colors aspect-square', h)}
          aria-label="Message"
        >
          <svg viewBox="0 0 24 18" className={icon} aria-hidden="true">
            <path fill="#1e90ff" stroke="#fff" strokeWidth="1.5" strokeLinejoin="round" d="M1 1h22v16H1z" />
            <path fill="none" stroke="#fff" strokeWidth="1.5" strokeLinejoin="round" d="M1 1l11 9 11-9" />
          </svg>
        </button>
        {wa ? (
          <a
            href={`https://wa.me/${wa}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className={cn('inline-flex items-center justify-center rounded-md border border-primary/40 bg-background text-primary hover:bg-primary/5 transition-colors aspect-square', h)}
            aria-label="WhatsApp"
          >
            <svg viewBox="0 0 32 32" className={icon} aria-hidden="true">
              <path fill="#25D366" d="M16 .5C7.44.5.5 7.44.5 16c0 2.82.74 5.57 2.16 8L.5 31.5l7.74-2.03A15.46 15.46 0 0 0 16 31.5C24.56 31.5 31.5 24.56 31.5 16S24.56.5 16 .5z"/>
              <path fill="#fff" d="M23.47 19.4c-.39-.2-2.3-1.13-2.66-1.26-.36-.13-.62-.2-.88.2-.26.39-1 1.26-1.23 1.52-.23.26-.45.3-.84.1-.39-.2-1.65-.61-3.14-1.94-1.16-1.03-1.94-2.3-2.17-2.69-.23-.39-.02-.6.17-.8.18-.17.39-.45.59-.68.2-.23.26-.39.39-.65.13-.26.07-.49-.03-.68-.1-.2-.88-2.13-1.21-2.91-.32-.76-.64-.66-.88-.67l-.75-.01c-.26 0-.68.1-1.03.49-.35.39-1.35 1.32-1.35 3.22 0 1.9 1.38 3.74 1.58 4 .2.26 2.73 4.17 6.61 5.84.92.4 1.64.63 2.2.81.92.29 1.76.25 2.42.15.74-.11 2.3-.94 2.62-1.85.32-.91.32-1.69.23-1.85-.1-.16-.36-.26-.75-.45z"/>
            </svg>
          </a>
        ) : (
          <button
            type="button"
            disabled
            className={cn('inline-flex items-center justify-center rounded-md border border-muted bg-background text-muted-foreground opacity-60 aspect-square', h)}
            aria-label="WhatsApp unavailable"
          >
            <svg viewBox="0 0 24 24" className={icon} fill="currentColor" aria-hidden="true">
              <path d="M20.52 3.48A11.94 11.94 0 0 0 12.06 0C5.5 0 .16 5.34.16 11.9c0 2.1.55 4.15 1.6 5.96L0 24l6.32-1.66a11.9 11.9 0 0 0 5.74 1.46h.01c6.56 0 11.9-5.34 11.9-11.9 0-3.18-1.24-6.17-3.45-8.42z" />
            </svg>
          </button>
        )}
        <button
          type="button"
          onClick={(e) => { stop(e); setCallOpen(true); }}
          className={cn('flex-1 inline-flex items-center justify-center gap-1.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors text-xs font-bold uppercase tracking-wide', h)}
          aria-label="Call"
        >
          <Phone className={icon} />
          <span>Call</span>
        </button>
      </div>

      <CallContactDialog
        open={callOpen}
        onOpenChange={setCallOpen}
        agentName={agentName || 'Property Agent'}
        agencyName={agencyName || undefined}
        mobileNumber={phone || undefined}
        phoneNumber={phone || undefined}
        referenceId={referenceId || (propertyId || projectId || '').slice(0, 8) || 'N/A'}
        listingId={propertyId || projectId}
        listingType={listingType}
      />
      <EmailContactDialog
        open={msgOpen}
        onOpenChange={setMsgOpen}
        agentName={agentName || 'Property Agent'}
        agencyName={agencyName || undefined}
        agentLogo={agentLogo || undefined}
        phoneNumber={phone || undefined}
        referenceId={referenceId || (propertyId || projectId || '').slice(0, 8) || 'N/A'}
        propertyId={listingType === 'property' || listingType === 'commercial' || listingType === 'plot' ? propertyId : undefined}
        projectId={listingType === 'project' ? projectId : undefined}
      />
    </>
  );
}
